import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  TemplateRef,
  viewChild,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { StandardQuoteService } from '../standard-quote-service.service';
import { finalize, Subject } from 'rxjs';
import { AuthService } from '@app/common/auth.service';
import { BaseComponent } from '@app/components/base/base.component';
import { modelTypeEnum, priceTypeEnum } from '../model/standard-quote.model';
import { ModalService } from '@app/components/modal/modal.service';
import { FileType, SubBrandService } from '@app/services/subbrand.service';
import { Constants } from '@app/constants/Constants';

@Component({
  template: ""
})
export class ModelsWrapperComponent extends BaseComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly fb = inject(FormBuilder);
  public readonly authService = inject(AuthService);
  private readonly subBrandService = inject(SubBrandService);
  public readonly quoteService = inject(StandardQuoteService);
  private readonly destroy = inject(DestroyRef);

  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;
  @Input() ready: Subject<boolean>;
  @Output() updateForm: EventEmitter<any> = new EventEmitter();
  @Output() setQuantity: EventEmitter<any> = new EventEmitter();
  @Output() deleteAllDeals: EventEmitter<any> = new EventEmitter();
  creationUser: number;
  hasDeals: boolean;
  subBrandImg = signal('');

  form: FormGroup;
  originalValues: any;
  isLoading: boolean = false;

  modelOptions: any[] = [];
  ownerUser: boolean = false;

  modelyears = signal<number[]>([]);

  allModels: any[] = [];
  isDraft: boolean = false;

  readonly confirmOpen = signal(false);
  readonly fieldSelected = signal('');
  readonly template = viewChild<TemplateRef<any>>('templateConfirmation');
  private isSensitiveActionConfirmed = false;
  event: any;
  readonly ConstantsComp = Constants;

  constructor(
    route: ActivatedRoute
  ) {
    super(route);
    this.createForm();
    const idQuote = this.route?.snapshot.params['id'];
    if (!idQuote) {
      this.ownerUser = true;
    }
  }

  ngOnInit() {
    this.isSensitiveActionConfirmed = true;
    this.ready.subscribe((value: any) => {
      if (value) {
        this.isLoading = true;
        this.creationUser = value.creationUser;
        this.ownerUser = value.creationUser == this.authService.currentUserIds;
        this.hasDeals = value.hasDeals;
        this.quoteService
          .getModelsByFilter({ modelType: value.modelType, classification: value.classification })
          .pipe(finalize(() => this.isLoading = false))
          .subscribe({
            next: (response) => {
              this.allModels = response.data;
              this.quoteService.models = response.data;
              this.originalValues = value;
              this.originalValues.brand = value.brand;
              this.originalValues.subBrand = value.subBrand;
              if (
                !this.originalValues.quantity ||
                this.originalValues.quantity < Constants.MIN_QUANTITY_FOR_NORMAL_PRODUCT
              ) {
                this.originalValues.quantity =
                  Constants.MIN_QUANTITY_FOR_NORMAL_PRODUCT;
              }
              this.form.patchValue(this.originalValues);
              this.hasDeals = value.hasDeals;
              this.filterYears();
              this.filterModels(value.year);
              this.getFile(value?.subBrand?.id || 0);

              this.isDraft = this.quoteService?.formData?.status == 'DRAFT';
            },
          });
      }
    });
    this.form.valueChanges.subscribe(() => {
      if (this.form.valid)
        this.updateForm.emit({ model: this.form.getRawValue() });
    });
  }

  getFile(id: number) {
    if (id === 0 || !this.isSensitiveActionConfirmed) return;
    this.isLoading = true;
    this.subBrandService
      .getAttachmentByElementId(id, FileType.SUB_BRAND)
      .subscribe({
        next: (response: any) => {
          if (response.data.length > 0) {
            const row = response.data[0];
            this.subBrandImg.set(row.publicUrl || row.urlSource || '');
          } else {
            this.subBrandImg.set('');
          }
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.subBrandImg.set('');
          if (error.status === 404) {
            return;
          }
        },
      });
  }

  createForm() {
    this.form = this.fb.group({
      id: ['', [Validators.required]],
      name: ['', [Validators.required]],
      modelCode: ['', [Validators.required]],
      enabled: ['', [Validators.required]],
      modelType: ['', [Validators.required]],
      classification: ['', [Validators.required]],
      year: ['', [Validators.required]],
      quantity: [
        Constants.MIN_QUANTITY_FOR_NORMAL_PRODUCT,
        [
          Validators.required,
          Validators.min(Constants.MIN_QUANTITY_FOR_NORMAL_PRODUCT),
          Validators.pattern(/^[1-9]\d*$/),
          Validators.max(Constants.MAX_QUANTITY_FOR_NORMAL_PRODUCT),
        ],
      ],
      price: ['', [Validators.required, Validators.min(Constants.MIN_UNIT_PRICE_FOR_PRODUCTS)]],
      priceType: [priceTypeEnum.priceNormal, [Validators.required]],
      currency: ['', [Validators.required]],
      subBrand: ['', [Validators.required]],
      brand: ['', [Validators.required]],
    });
  }

  fetchData() {
    const modelType = this.form.controls['modelType'].value;
    const classification = this.form.controls['classification'].value;

    this.isLoading = true;
    return this.quoteService
      .getModelsByFilter({ modelType, classification })
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (response) => {
          this.allModels = response.data;
          this.quoteService.models = this.allModels;
          this.filterYears();
          this.filterModels();
        },
      });
  }

  filterYears() {
    const modelType = this.form.controls['modelType'].value;
    const classification = this.form.controls['classification'].value;
    this.modelyears.set([
      ...new Set(
        this.allModels
          .filter(
            (i) =>
              i.modelType === modelType && (!this.isAccesoriesProduct ? i.classification === classification : true
          ))
          .map((i) => i.year)
      ),
    ].sort());
  }

  filterModels(year?: number) {
    const modelType = this.form.controls['modelType'].value;
    const classification = this.form.controls['classification'].value;

    this.modelOptions = this.allModels
      .filter(
        (i) =>
          i.modelType === modelType &&
          (!this.isAccesoriesProduct ? i.classification === classification : true) &&
          (year ? i.year == year : true) &&
          i.enabled
      )
      .map((i) => ({
        label: i.name,
        value: i.id,
        modelCode: i.modelCode,
        modelType: i.modelType,
        classification: i.classification,
        year: i.year,
        enabled: i.enabled,
      }));
  }

  cleanModelData() {
    this.form.controls['id'].setValue(null);
    this.form.controls['name'].setValue(null);
    this.form.controls['modelCode'].setValue(null);
    this.form.controls['price'].setValue(null);
    this.form.controls['currency'].setValue(null);
    this.form.controls['enabled'].setValue(null);
    this.form.controls['brand'].setValue(null);
    this.form.controls['subBrand'].setValue(null);
    this.syncModelToQuoteForm();
  }

  private isInnerModelSelected(raw: ReturnType<typeof this.form.getRawValue>): boolean {
    return !!(
      raw?.id &&
      raw?.name &&
      raw?.modelCode &&
      raw?.price != null &&
      raw?.currency
    );
  }

  private syncModelToQuoteForm(): void {
    const raw = this.form.getRawValue();
    this.updateForm.emit({
      model: this.isInnerModelSelected(raw) ? raw : null,
    });
  }

  getClassificationForAccesories() {
    const year = this.form.controls['year'].value;
    if(!year){
      return null;
    }
    const now = new Date();
    const nowYear = now.getFullYear();
    const nowMonth = now.getMonth() + 1;
    const isCutUsedModels = nowMonth >= 10;
    const usedModelYears = isCutUsedModels ? nowYear-1 : nowYear-2;
    if(year <= usedModelYears){
      return false;
    }
    return true;
  }

  setClassificationForAccesories() {
    if(this.isAccesoriesProduct){
      const classification = this.getClassificationForAccesories();
      this.form.controls['classification'].setValue(classification);
      this.form.controls['classification'].updateValueAndValidity({onlySelf: true});
    }
  }

  fetchDataIfChangeModelOrClassification() {
    const modelType = this.form.controls['modelType'].value;
    const classification = this.form.controls['classification'].value;
    const isAccesoriesProduct = modelType == modelTypeEnum.accesorios;
    if(modelType && isAccesoriesProduct) {
      this.fetchData().add(() => {
        this.cleanModelData();
      });
    } else if(modelType && classification.toString()){
      this.fetchData();
      this.form.controls['year'].setValue(null);
      this.cleanModelData();
    }
  }

  changeModel(event: any) {
    if (!event) return;
    const modelSelected: any = this.allModels.find(
      (i) => i.modelCode == this.form.controls['modelCode'].value
    );
    const dataModel = {
      id: modelSelected.id,
      modelCode: modelSelected.modelCode,
      currency: modelSelected.currency,
      price: modelSelected.price,
      brand: modelSelected.subBrand.brand,
      subBrand: modelSelected.subBrand,
      name: modelSelected.name,
      enabled: modelSelected.enabled
    };
    this.quoteService.residualValue = modelSelected?.residualModelValues ?? [];
    this.form.patchValue(dataModel);
    this.changeSensibleData('name', event);
    this.getFile(modelSelected.subBrand.id || 0);
  }

  triggers: any = {
    modelType: () => {
      this.fetchDataIfChangeModelOrClassification();
    },
    classification: () => {
      this.fetchDataIfChangeModelOrClassification();
    },
    year: () => {
      const year = this.form.controls['year'].value;
      this.setClassificationForAccesories();
      this.filterModels(year);
      this.cleanModelData();
    },
    name: () => {},
    currency: () => {},
    price: () => {},
    quantity: () => {
      this.setQuantity.emit(this.normalizeQuantity(this.form.get('quantity')?.value));
    },
  };

  /** Mínimo 1: (change) puede emitir 0 antes del clamp de blur. */
  normalizeQuantity(value: unknown): number {
    const n = Number(value);
    const min = Constants.MIN_QUANTITY_FOR_NORMAL_PRODUCT;
    const qty = !Number.isFinite(n) || n < min ? min : Math.trunc(n);
    if (this.form.get('quantity')?.value !== qty) {
      this.form.controls['quantity'].setValue(qty);
    }
    return qty;
  }

  closeConfirmationDialog(result: boolean) {
    this.isSensitiveActionConfirmed = result;
    if (this.isSensitiveActionConfirmed) {
      this.subBrandImg.set('');
      this.triggers[this.fieldSelected()](this.event);
      this.syncModelToQuoteForm();
      this.deleteAllDeals.emit();
      this.hasDeals = false;
      this.originalValues = { ...this.form.getRawValue() };
    } else {
      this.isSensitiveActionConfirmed = false;
      this.form.patchValue(this.originalValues);
      if (this.fieldSelected() === 'name') {
        const modelSelected: any = this.allModels.find(
          (i) => i.modelCode == this.originalValues.modelCode
        );
        this.form.controls['modelCode'].setValue(modelSelected.modelCode);
        this.form.controls['price'].setValue(modelSelected.price);
      }
      this.form.updateValueAndValidity({ onlySelf: true });
    }
    this.getFile(this.form.get('subBrand')?.value?.id || 0);
    this.modalService.closeModal();
    this.fieldSelected.set('');
    this.confirmOpen.set(false);
    this.event = null;
  }

  changeSensibleData(field: string, event?: any) {
    if (field in this.triggers) {
      if (this.hasChanges() && this.hasDeals) {
        this.isSensitiveActionConfirmed = false;
        this.confirmOpen.set(true);
        this.fieldSelected.set(field);
        this.event = event;
        this.modalService.component = this.template()!;
        this.modalService.openModal();
      } else {
        this.isSensitiveActionConfirmed = true;
        this.triggers[field](event);
        if (
          field === 'year' ||
          field === 'classification' ||
          field === 'modelType'
        ) {
          this.subBrandImg.set('');
        }
      }
    }
  }

  hasChanges() {
    if (!this.originalValues) return false;
    return Object.keys(this.triggers).some(
      (key: any) => this.originalValues[key] !== this.form.getRawValue()[key]
    );
  }

  getDescriptionVehicle(modelType: any) {
    return this.quoteService.productType.find((i) => i.productType == modelType)
      ?.description;
  }

  getModelNameWithCode(model: any) {
    const codePart = model?.modelCode ?? '';
    const pattern = new RegExp(`\\|\\s*MB.*$`);
    const exactlyPattern = new RegExp(`\\s*${codePart}\\s*$`);

    let labelWithoutCode = model?.label?.replace?.(pattern, '')?.trim?.() ?? '';
    labelWithoutCode = labelWithoutCode.replace(exactlyPattern, '');
    return `${labelWithoutCode} | ${codePart}`;
  }

  get isAccesoriesProduct() {
    return this.form.controls['modelType'].value == modelTypeEnum.accesorios;
  }
  get formYear() {
    return this.form.controls['year'].value;
  }
}
